import styled from 'styled-components';
import { IconChevronRight } from '@shared/ui/icons';
import { Select } from '@shared/ui/Input';
import { fleetType } from '@entities/fleet';
import { periodLabel, periodOptions, shiftPeriod } from '@entities/chronum';
import { PERIOD_DEPTH } from './useChronumParams';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-width: 0;

  & > label {
    font-size: ${fleetType.small};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 0;
`;

const MonthSelect = styled(Select)`
  width: 176px;
  min-width: 0;
  flex: 1 1 auto;
  font-variant-numeric: tabular-nums;
`;

const Step = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover:not([aria-disabled='true']) {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Крайний месяц: кнопка остаётся в фокусе и видна рамкой, но ничего не делает
     (disabled уронил бы фокус на body посреди перелистывания). */
  &[aria-disabled='true'] {
    cursor: default;
    color: ${({ theme }) => theme.colors.text.muted};
    background: transparent;
  }

  & > .back {
    transform: scaleX(-1);
  }
`;

interface Props {
  id: string;
  period: string;
  /** Самый поздний месяц, который можно выбрать (текущий). */
  latest: string;
  onChange: (period: string) => void;
}

/** Выбор месяца: список за два года и шаги «назад / вперёд». */
export function PeriodPicker({ id, period, latest, onChange }: Props) {
  const options = periodOptions(latest, PERIOD_DEPTH);
  const list = options.includes(period) ? options : [period, ...options];
  const oldest = list[list.length - 1] ?? period;
  const prev = shiftPeriod(period, -1);
  const next = shiftPeriod(period, 1);
  const atStart = period <= oldest;
  const atEnd = next > latest;
  return (
    <Wrap>
      <label htmlFor={id}>Месяц</label>
      <Row>
        <Step
          type="button"
          aria-label={
            atStart ? 'Более ранних месяцев в списке нет' : `Предыдущий месяц: ${periodLabel(prev)}`
          }
          aria-disabled={atStart}
          onClick={() => {
            if (!atStart) onChange(prev);
          }}
        >
          <IconChevronRight className="back" size={18} />
        </Step>
        <MonthSelect id={id} value={period} onChange={(e) => onChange(e.target.value)}>
          {list.map((p) => (
            <option key={p} value={p}>
              {periodLabel(p)}
            </option>
          ))}
        </MonthSelect>
        <Step
          type="button"
          aria-label={atEnd ? 'Следующего месяца ещё нет' : `Следующий месяц: ${periodLabel(next)}`}
          aria-disabled={atEnd}
          onClick={() => {
            if (!atEnd) onChange(next);
          }}
        >
          <IconChevronRight size={18} />
        </Step>
      </Row>
    </Wrap>
  );
}
